import { useRouter } from 'next/router'
import { Button, Input } from '@gate/gui'
import { useTranslation } from 'next-i18next'
import { COIN_ICON_URL } from '@/constants/index'
import { useStore } from '@/store/index'
import { useState } from 'react'
import useLangPrefix from '@/hooks/useLangPrefix'
import { formatAddString, deepClone, coinImageUrl } from '@/utils/tools'
import { Flex } from '@mantine/core'
import BaseDrawer from '@/components/BaseDrawer'
import { useAdapter } from '@/components/Adapter'
import BaseImage from '@/components/BaseImage'
import BaseTooltip from '@/components/BaseTooltip'
import { useDebounceFn, useUpdateEffect } from 'ahooks'
import { getPriceList } from 'api/price'
import BaseEmpty from '@/components/BaseEmpty'
import BaseRiseColor from '@/components/BaseRiseColor'
import { getCoinNameLanguage } from 'api/base'
import { SearchItemStyled, SearchListStyled, SearchWrapStyled } from './style'
import type { BasePriceItem } from 'api-price'
import { observer } from 'mobx-react-lite'

const SearchList = () => {
  const [searchValue, setSearchValue] = useState('')
  const { t } = useTranslation()
  const { isH5 } = useAdapter()
  const { isZh, langPrefix } = useLangPrefix()

  const { locale = 'en' } = useRouter()
  const { list, priceTab } = useStore().baseStore

  const currentList =
    list?.filter((item, index, self) => index === self.findIndex(t => t?.gate_id === item?.gate_id)) ?? []

  // todo 搜索框逻辑
  const [searchList, setSearchList] = useState(currentList)

  const { run } = useDebounceFn(
    async keyword => {
      try {
        if (keyword?.length) {
          const res = await getPriceList({ page: 1, pageSize: 20, keyword }, '' as any, locale)
          setSearchList(res?.list ?? [])
        } else {
          setSearchList(currentList)
        }
      } catch {
        setSearchList(currentList)
      }
    },
    { wait: 500 }
  )

  useUpdateEffect(() => {
    setSearchValue('')
  }, [priceTab])

  useUpdateEffect(() => {
    if (searchValue?.length > 0) {
      const gateIdsList = searchList?.map?.((i: BasePriceItem) => i?.gate_id)

      getCoinNameLanguage({ lang: locale, gateIds: gateIdsList }).then(coin => {
        const currentSearchList = deepClone(searchList)?.map(item => {
          return {
            ...item,
            currency:
              coin?.[item?.gate_id]?.[locale ?? ''] ??
              (isZh ? (coin?.[item?.gate_id]?.zh ?? coin?.[item?.gate_id]?.en) : coin?.[item?.gate_id]?.en) ??
              item?.currency,
          }
        })
        setSearchList(currentSearchList)
      })
    } else {
      const gateIds = currentList?.map?.((i: BasePriceItem) => i?.gate_id)
      getCoinNameLanguage({ lang: locale, gateIds }).then(coin => {
        const currentSearchList = deepClone(currentList)?.map(item => {
          return {
            ...item,
            currency:
              coin?.[item?.gate_id]?.[locale ?? ''] ??
              (isZh ? (coin?.[item?.gate_id]?.zh ?? coin?.[item?.gate_id]?.en) : coin?.[item?.gate_id]?.en) ??
              item?.currency,
          }
        })
        setSearchList(currentSearchList)
      })
    }
  }, [locale, list])

  return (
    <BaseDrawer
      isScroll={false}
      isButton={false}
      isRight={false}
      size={'80vh'}
      title={t('P.Search')}
      target={
        isH5 ? (
          <Button
            rightIcon={
              <svg className="iconpark-icon size-14">
                <use href="#v31-16px-line-sousuo"></use>
              </svg>
            }
            type="text"
          />
        ) : (
          <Input
            styles={{ wrapper: { width: '240px' } }}
            icon={
              <svg className="iconpark-icon size-14">
                <use href="#v31-16px-line-sousuo"></use>
              </svg>
            }
            placeholder={t('P.Search') + ''}
            value={searchValue}
            onChange={(e: any) => {
              const value = e.target.value
              setSearchValue(value)
              run(value)
            }}
            onClear={() => {
              setSearchValue('')
              run('')
            }}
          />
        )
      }
    >
      <SearchWrapStyled>
        {isH5 ? (
          <Flex px={12} pb={12}>
            <Input
              styles={{ wrapper: { width: '100%' } }}
              icon={
                <svg className="iconpark-icon size-14">
                  <use href="#v31-16px-line-sousuo"></use>
                </svg>
              }
              placeholder={t('P.Search') + ''}
              value={searchValue}
              onChange={(e: any) => {
                const value = e.target.value
                setSearchValue(value)
                run(value)
              }}
              onClear={() => {
                setSearchValue('')
                run('')
              }}
            />
          </Flex>
        ) : null}
        <SearchListStyled>
          {searchList.length ? (
            <>
              {searchList?.map((item, index) => (
                <SearchItemStyled key={item?.id + index} href={`${langPrefix}/price/${item?.seo_path}`}>
                  <Flex align="center" gap={isH5 ? 8 : 4}>
                    <BaseImage
                      isRadius
                      width={32}
                      height={32}
                      alt={item?.crypto}
                      src={coinImageUrl(item?.logo_url, item?.seo_path)}
                      coin={item?.seo_path}
                      loading="eager"
                    />

                    <Flex direction="column" gap={{ base: 2, sm: 4 }}>
                      <BaseTooltip label={item?.crypto} className="text-1 size-16 font-500 line-120 currency-price">
                        {item?.crypto}
                      </BaseTooltip>

                      <BaseTooltip label={item?.currency} className="text-3 size-14 font-400 line-120 currency-price">
                        {item?.currency}
                      </BaseTooltip>
                    </Flex>
                  </Flex>
                  <BaseRiseColor value={item?.change}>
                    <span className="text-1 size-16 font-500">{formatAddString(parseFloat(item?.change))}</span>
                  </BaseRiseColor>
                </SearchItemStyled>
              ))}
            </>
          ) : (
            <Flex py={24}>
              <BaseEmpty type="search" />
            </Flex>
          )}
        </SearchListStyled>
      </SearchWrapStyled>
    </BaseDrawer>
  )
}

export default observer(SearchList)
